import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { RootState } from "store/rootReducer";
import { selectTxnId, useMapStore } from "stores/map";
import {
  applyFieldNameMode,
  resolveSimplifyCustomFieldNames
} from "utils/column/getColumnLabelWithContext";

import { applyRemoveUUID } from "components/charts/utils";
import { addNormalizationBasisToTitle } from "components/charts/utils/addNormalizationBasisToTitle";
import { useScreenshotContext } from "components/screenshot/hooks";
import { applyAbbreviations } from "components/screenshot/utils";
import { useGlobalNormalizeBy } from "components/shared/charts/hooks";
import { useUserSettings } from "components/user/hooks";

import { EntityKind } from "../../models/entityKind";

const useLegendTitle = (entityKind: EntityKind = EntityKind.Well): string => {
  // state from store
  const txnId = useMapStore(selectTxnId);
  const globalGroupBy = useSelector((state: RootState) =>
    entityKind === EntityKind.Well
      ? state.groupBy.globalGroupBy
      : state.groupBy.globalFacilityFocus
  );
  const globalNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.globalNormalizeBy
  );
  const globalUseNormalizeBy = useSelector(
    (state: RootState) => state.normalizeBy.useNormalizeBy
  );
  const { userAbbreviations } = useUserSettings();
  const screenshotState = useScreenshotContext();

  // state
  const [value, setValue] = useState<string>("");

  useEffect(() => {
    const legendTitle =
      entityKind === EntityKind.Well ? txnId.legend.title : txnId.facilityLegend?.title;

    if (!legendTitle) {
      setValue("");
      return;
    }

    const title = globalGroupBy.title;

    const isForecastEnabled = title.includes("Forecast");
    let nextTitle = isForecastEnabled ? applyRemoveUUID(title) : title;

    // EVA-127 "Simplify Custom Field Names": single source of truth lives in
    // applyFieldNameMode. `undefined` (not in the map screenshot widget) is
    // interpreted identically to OFF — subgroup-qualified for custom fields
    // (never the category — EVA-6338), decorated as usual for every other
    // type; ON strips down to `{field} ({unit})`.
    const simplifyCustomFieldNames = resolveSimplifyCustomFieldNames(
      screenshotState.widget?.widgetId === "mb",
      screenshotState.settings
    );
    // Must run BEFORE addNormalizationBasisToTitle below: applyFieldNameMode is
    // what appends the unit for a custom field (Phase 1 dropped it from the
    // stored title). addNormalizationBasisToTitle finds a known unit token
    // INSIDE the title and rewrites it in place — running it first found no
    // unit yet, so `String.replace(undefined, …)` no-op'd and the normalization
    // basis silently never appeared for a normalized custom field.
    nextTitle = applyFieldNameMode(nextTitle, globalGroupBy, simplifyCustomFieldNames);

    nextTitle = globalUseNormalizeBy
      ? addNormalizationBasisToTitle(nextTitle, globalNormalizeBy)
      : nextTitle;

    if (
      screenshotState.widget?.widgetId === "mb" &&
      screenshotState.settings?.applyAbbreviations
    ) {
      nextTitle = applyAbbreviations(nextTitle, userAbbreviations?.abbreviations);
    }

    setValue(nextTitle);
  }, [
    entityKind,
    globalGroupBy,
    txnId.legend.title,
    txnId.facilityLegend?.title,
    useGlobalNormalizeBy,
    globalNormalizeBy,
    screenshotState.widget?.widgetId,
    screenshotState.settings?.simplifyCustomFieldNames,
    screenshotState.settings?.applyAbbreviations,
    userAbbreviations?.abbreviations
  ]);

  return value;
};

export default useLegendTitle;
